import { useCallback, useState } from 'react';
import { Image, ScrollView, Text, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { getSubmission } from '../api/submissions';
import { resolveDispute } from '../api/admin';
import { Submission } from '../types';
import { Button, Card, ConfirmDialog, ErrorText, FormInput, Label, LoadingState, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { resolvePhotoUrl } from '../config';
import { openExternalUrl } from '../utils/openUrl';
import { DISPUTE_STATUS_LABELS } from '../constants/labels';

export default function AdminSubmissionDisputeScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { token } = useAuth();
  const router = useRouter();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingResolve, setPendingResolve] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      setSubmission(await getSubmission(token, id));
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được bài nộp, vui lòng thử lại.');
    }
  }, [token, id]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onResolve = (forReviewer: boolean) => setPendingResolve(forReviewer);

  const confirmResolve = async () => {
    if (!token || !submission || pendingResolve === null) return;
    setBusy(true);
    setError(null);
    try {
      await resolveDispute(token, submission.id, pendingResolve, adminNote.trim() || undefined);
      router.back();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Xử lý khiếu nại thất bại');
    } finally {
      setBusy(false);
      setPendingResolve(null);
    }
  };

  if (!submission) {
    if (loadError) {
      return (
        <ScreenContainer>
          <ErrorText>{loadError}</ErrorText>
          <Button title="Thử lại" onPress={load} />
        </ScreenContainer>
      );
    }
    return <LoadingState />;
  }
  const task = submission.task!;
  const isPendingDispute = submission.disputeStatus === 'OPEN';

  return (
    <ScreenContainer>
      <ScrollView>
        <Card>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text, flex: 1 }}>
              {task.title}
            </Text>
            <StatusBadge status={submission.status} />
          </View>
          <Text style={{ color: colors.textMuted, marginTop: 4 }}>Shop: {task.shop?.name}</Text>
          <Text style={{ color: colors.textMuted }}>
            Người review: {submission.reviewer?.name} ({submission.reviewer?.email})
          </Text>
          {submission.checkinDistanceMeters != null ? (
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>
              Check-in cách shop {Math.round(submission.checkinDistanceMeters)}m
            </Text>
          ) : null}
          {submission.checkinAt ? (
            <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 12 }}>
              Check-in lúc: {new Date(submission.checkinAt).toLocaleString('vi-VN')}
            </Text>
          ) : null}
        </Card>

        <Card style={{ borderColor: colors.warning, borderWidth: 1 }}>
          <Text style={{ fontFamily: fonts.bold, color: colors.warning, marginBottom: 6 }}>
            Lý do bị từ chối (chủ shop)
          </Text>
          <Text style={{ color: colors.text }}>{submission.rejectReason || '—'}</Text>
          <Text style={{ fontFamily: fonts.bold, color: colors.warning, marginTop: 12, marginBottom: 6 }}>
            Lý do khiếu nại (người review)
          </Text>
          <Text style={{ color: colors.text }}>{submission.disputeReason || '—'}</Text>
          {submission.disputedAt ? (
            <Text style={{ color: colors.textMuted, marginTop: 6, fontSize: 12 }}>
              Khiếu nại lúc: {new Date(submission.disputedAt).toLocaleString('vi-VN')}
            </Text>
          ) : null}
        </Card>

        <Card>
          <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 8 }}>
            Ảnh minh chứng ({submission.photos.length})
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {submission.photos.map((p) => (
              <Image
                key={p.id}
                source={{ uri: resolvePhotoUrl(p.url) }}
                style={{ width: 110, height: 110, borderRadius: 8 }}
              />
            ))}
          </View>
        </Card>

        {submission.googleReviewUrl ? (
          <Card>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 8 }}>
              Link bài review Google Maps
            </Text>
            <Text style={{ color: colors.primary }} onPress={() => openExternalUrl(submission.googleReviewUrl!)}>
              {submission.googleReviewUrl}
            </Text>
          </Card>
        ) : null}

        <ErrorText>{error}</ErrorText>

        {isPendingDispute ? (
          <Card>
            <Label>Ghi chú xử lý (tuỳ chọn, người review và chủ shop đều thấy được)</Label>
            <FormInput
              value={adminNote}
              onChangeText={setAdminNote}
              placeholder="Ví dụ: ảnh minh chứng hợp lệ, chủ shop từ chối không có căn cứ"
              multiline
            />
            <Button
              title="Chấp nhận khiếu nại — duyệt & trả thưởng"
              onPress={() => onResolve(true)}
              loading={busy}
            />
            <Button
              title="Từ chối khiếu nại — giữ nguyên quyết định"
              variant="danger"
              onPress={() => onResolve(false)}
              loading={busy}
            />
          </Card>
        ) : (
          <Card>
            <Text style={{ color: colors.textMuted }}>
              Khiếu nại này đã được xử lý ({DISPUTE_STATUS_LABELS[submission.disputeStatus ?? ''] ?? submission.disputeStatus}).
            </Text>
            {submission.disputeAdminNote ? (
              <Text style={{ color: colors.textMuted, marginTop: 6, fontSize: 13 }}>
                Ghi chú: {submission.disputeAdminNote}
              </Text>
            ) : null}
          </Card>
        )}
      </ScrollView>
      <ConfirmDialog
        visible={pendingResolve !== null}
        title={pendingResolve ? 'Xác nhận chấp nhận khiếu nại' : 'Xác nhận từ chối khiếu nại'}
        message={
          pendingResolve
            ? `Chấp nhận khiếu nại và trả thưởng ${submission.task?.rewardCoins ?? ''} xu cho "${submission.reviewer?.name}"? Thao tác này không thể hoàn tác.`
            : `Từ chối khiếu nại và giữ nguyên quyết định từ chối bài nộp của "${submission.reviewer?.name}"?`
        }
        confirmLabel={pendingResolve ? 'Duyệt & trả thưởng' : 'Từ chối khiếu nại'}
        danger={!pendingResolve}
        loading={busy}
        onConfirm={confirmResolve}
        onCancel={() => setPendingResolve(null)}
      />
    </ScreenContainer>
  );
}
